import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import { useState } from 'react';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import type { DialogProps } from './Dialog';
import { BottomSheetProvider } from './sheetContext';
import { useSheetContent } from './useSheetContent';
import { SHEET_EASING, useSheetDrag } from './useSheetDrag';

const SHEET_TIMEOUT = { enter: 280, exit: 200 };

type BottomSheetProps = Pick<
  DialogProps,
  'open' | 'onClose' | 'disableEscapeKeyDown' | 'disableEnforceFocus' | 'keepMounted' | 'children'
> & {
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
};

/** Mobile rendering of `Dialog` — see `useSheetDrag` for the gesture rules. */
export function BottomSheet({
  open,
  onClose,
  disableEscapeKeyDown,
  disableEnforceFocus,
  keepMounted,
  children,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy
}: BottomSheetProps) {
  // State, not a ref: the Modal portal mounts the paper after this component's first commit.
  const [paper, setPaper] = useState<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const content = useSheetContent(open, keepMounted, children);

  useSheetDrag({
    paper,
    open,
    expanded,
    onExpandedChange: setExpanded,
    onDismiss: () => onClose?.({}, 'swipeDown')
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={disableEscapeKeyDown}
      disableEnforceFocus={disableEnforceFocus}
      keepMounted={keepMounted}
      closeAfterTransition
      // Fading backdrop (like MUI Dialog's); tapping it closes with reason 'backdropClick'.
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { transitionDuration: SHEET_TIMEOUT } }}
      className="SmBottomSheet-modal"
    >
      <Slide
        direction="up"
        in={open}
        timeout={SHEET_TIMEOUT}
        easing={SHEET_EASING}
        onExited={() => {
          setExpanded(false);
          content.onExited();
        }}
      >
        <Paper
          ref={setPaper}
          role="dialog"
          aria-modal
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          elevation={0}
          className={['SmBottomSheet-paper', expanded && 'SmBottomSheet-expanded'].filter(Boolean).join(' ')}
        >
          <div className="SmBottomSheet-header">
            {expanded ? (
              <IconButton
                variant="text"
                color="secondary"
                shape="circle"
                size="medium"
                aria-label="סגור"
                onClick={event => onClose?.(event, 'closeButton')}
              >
                <Icon icon="closeOutlined" color="inherit" />
              </IconButton>
            ) : (
              <span className="SmBottomSheet-handle" aria-hidden />
            )}
          </div>
          <div className="SmBottomSheet-body">
            <BottomSheetProvider value={true}>{content.content}</BottomSheetProvider>
          </div>
        </Paper>
      </Slide>
    </Modal>
  );
}
